import {
    ActionIcon,
    Box,
    Checkbox,
    CloseButton,
    Flex,
    Group,
    getRadius,
    type MantineColorScheme,
    type MantineTheme,
    Stack,
    Text,
    TextInput,
    Tooltip,
    useMantineColorScheme,
    useMantineTheme,
} from '@mantine/core';
import {
    useColorScheme,
    useDebouncedValue,
    useDisclosure,
} from '@mantine/hooks';
import {
    IconCheck,
    IconChevronDown,
    IconChevronRight,
    IconDots,
    IconFilter,
    IconFilterFilled,
    IconPlus,
    IconSearch,
    IconSettings,
} from '@tabler/icons-react';
import { useContextMenu } from 'mantine-contextmenu';
import {
    type MouseEvent,
    type MouseEventHandler,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { SetState } from '../../../types/setState';
import type { OptionalProps } from '../../../utils/optionalProps';
import { filterByString, isFunction } from '../../../utils/utilFunctions';
import type {
    RenderTreeNodeElements,
    SerializableRenderTreeNode,
} from './RenderTreeNode';
import { VirtualizedList } from './VirtualizedList';

const NODE_HEIGHT = 26;

const nodesToOpenAtNode = <D, K extends string>(
    selectedKey: string,
    nodes: Array<SerializableRenderTreeNode<D, K>>
): Array<string> | undefined => {
    const nodesToOpen = new Array<string>();

    const findInOriginal = (
        searchedNodes: Array<SerializableRenderTreeNode<D, K>>
    ): boolean => {
        const result = searchedNodes.find(
            (n) =>
                n.key === selectedKey ||
                (n.children?.length && findInOriginal(n.children))
        );
        if (result && result.key !== selectedKey) {
            nodesToOpen.push(result.key);
        }
        return Boolean(result);
    };
    const result = findInOriginal(nodes);

    return result ? nodesToOpen : undefined;
};

type FlattenedNode<D, K extends string> = {
    originalNode: SerializableRenderTreeNode<D, K>;
    depth: number;
    opened: boolean;
};

export type FilterOption<D, K extends string> = {
    label: string;
    onFilter: (
        node: SerializableRenderTreeNode<D, K>,
        treeProps: MantineTreeProps<D, K>
    ) => boolean;
    disabled: boolean;
};

const useExternalState = <T extends NonNullable<unknown> | null>({
    defaultValue,
    value,
    onChange,
}: {
    defaultValue: NoInfer<T>;
    value: T | undefined;
    onChange: ((newValue: NoInfer<T>) => void) | undefined;
}) => {
    const [state, setState] = useState(defaultValue);

    const handleChange: SetState<T> = useCallback(
        (setter) => {
            if (value !== undefined && onChange !== undefined) {
                onChange(isFunction(setter) ? (setter(value) as T) : setter);
            }
        },
        [onChange, value]
    );

    if (value !== undefined && onChange !== undefined) {
        return [value, handleChange] as const;
    } else {
        return [state, setState] as const;
    }
};

export const Tree = <D, K extends string>({
    nodes,
    filtered,
    onAddClick,
    onEllipsisClick,
    filterOptions,
    supportsMultipleFilterOptions,
    withFilterConfiguration,
    customButtons,
    ...props
}: MantineTreeProps<D, K> &
    (
        | {
              filtered: true;
              filterOptions?: Array<FilterOption<D, K>>;
              supportsMultipleFilterOptions?: boolean;
              withFilterConfiguration?: boolean;
              onAddClick?: MouseEventHandler<unknown> | undefined;
              onEllipsisClick?: MouseEventHandler | undefined;
              customButtons?: ReactNode;
          }
        | {
              filtered?: false;
              filterOptions?: never;
              supportsMultipleFilterOptions?: never;
              withFilterConfiguration?: never;
              onAddClick?: never;
              onEllipsisClick?: never;
              customButtons?: never;
          }
    )) => {
    const [filter, setFilter] = useState('');
    const [activeFilterOption, setActiveFilterOption] = useState(
        new Array<FilterOption<D, K>>()
    );
    const [includeChildrenInFilterOption, setIncludeChildrenInFilterOption] =
        useState<boolean>(false);
    const [debouncedFilter] = useDebouncedValue(filter, 400);
    const { showContextMenu } = useContextMenu();

    const getFilterOptionsMenuItems = () => {
        if (filterOptions) {
            return filterOptions.map((filterOption) => ({
                key: filterOption.label,
                title: filterOption.label,
                icon: activeFilterOption.find(
                    (value) => value.label === filterOption.label
                ) ? (
                    <IconCheck />
                ) : undefined,
                onClick: () => {
                    if (
                        activeFilterOption.find(
                            (value) => value.label === filterOption.label
                        )
                    ) {
                        setActiveFilterOption(
                            activeFilterOption.filter(
                                (value) => value.label !== filterOption.label
                            )
                        );
                    } else {
                        if (supportsMultipleFilterOptions) {
                            setActiveFilterOption([
                                ...activeFilterOption,
                                filterOption,
                            ]);
                        } else {
                            setActiveFilterOption([filterOption]);
                        }
                    }
                },
            }));
        }
        return [];
    };

    const getFilterConfigurationMenuItems = () => {
        return [
            {
                key: 'include children',
                title: 'Include Children',
                icon: includeChildrenInFilterOption ? <IconCheck /> : undefined,
                onClick: () => {
                    setIncludeChildrenInFilterOption(
                        !includeChildrenInFilterOption
                    );
                },
            },
        ];
    };

    const filteredNodes = useMemo(() => {
        if (!filtered || !debouncedFilter) {
            return nodes;
        }

        const filterFn = (
            originalNodes: Array<SerializableRenderTreeNode<D, K>>
        ): Array<SerializableRenderTreeNode<D, K>> => {
            return originalNodes.mapNotNull(({ children, ...node }) => {
                const filteredChildren = children?.let(filterFn);

                const filteredByFilterOption =
                    activeFilterOption.length &&
                    !activeFilterOption.some((value) =>
                        value.onFilter(node, { ...props, nodes })
                    );

                if (filteredChildren?.length) {
                    if (
                        includeChildrenInFilterOption &&
                        !filteredByFilterOption &&
                        filterByString(node.label, debouncedFilter, true)
                    ) {
                        return { children: children, ...node };
                    }
                    return { children: filteredChildren, ...node };
                }

                if (
                    filteredByFilterOption ||
                    !filterByString(node.label, debouncedFilter, true)
                ) {
                    return null;
                }

                return {
                    children: includeChildrenInFilterOption
                        ? children
                        : filteredChildren,
                    ...node,
                };
            });
        };

        return filterFn(nodes);
    }, [
        activeFilterOption,
        debouncedFilter,
        filtered,
        nodes,
        includeChildrenInFilterOption,
    ]);

    return (
        <Stack
            h={'100%'}
            gap={4}
        >
            {filtered && (
                <Group
                    wrap={'nowrap'}
                    gap={4}
                >
                    <TextInput
                        placeholder={'Search'}
                        size={'xs'}
                        style={{ flexGrow: 1 }}
                        value={filter}
                        onChange={(e) => setFilter(e.currentTarget.value)}
                        rightSection={
                            filter ? (
                                <CloseButton
                                    c={'gray'}
                                    variant="transparent"
                                    onClick={() => setFilter('')}
                                />
                            ) : (
                                <IconSearch />
                            )
                        }
                    />
                    {customButtons}
                    {onAddClick && (
                        <ActionIcon
                            variant={'transparent'}
                            size={'sm'}
                            onClick={onAddClick}
                            c={'var(--mantine-color-base-button)'}
                        >
                            <IconPlus />
                        </ActionIcon>
                    )}
                    {filterOptions && (
                        <Tooltip label={'Filter Options'}>
                            <ActionIcon
                                variant={'transparent'}
                                size={'sm'}
                                onClick={showContextMenu(
                                    getFilterOptionsMenuItems()
                                )}
                                c={'var(--mantine-color-base-button)'}
                            >
                                {activeFilterOption.length ? (
                                    <IconFilterFilled />
                                ) : (
                                    <IconFilter />
                                )}
                            </ActionIcon>
                        </Tooltip>
                    )}
                    {withFilterConfiguration && (
                        <Tooltip label={'Filter and Search Configuration'}>
                            <ActionIcon
                                variant={'transparent'}
                                size={'sm'}
                                onClick={showContextMenu(
                                    getFilterConfigurationMenuItems()
                                )}
                                c={'var(--mantine-color-base-button)'}
                            >
                                <IconSettings />
                            </ActionIcon>
                        </Tooltip>
                    )}
                    {onEllipsisClick && (
                        <ActionIcon
                            variant={'transparent'}
                            size={'sm'}
                            onClick={onEllipsisClick}
                            c={'var(--mantine-color-base-button)'}
                        >
                            <IconDots />
                        </ActionIcon>
                    )}
                </Group>
            )}
            <Box
                style={{ flexGrow: 1, position: 'relative' }}
                w={'100%'}
            >
                <Box
                    style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: 0,
                        bottom: 0,
                    }}
                >
                    <MantineTree
                        {...props}
                        nodes={filteredNodes}
                        activeFilterOptionFn={
                            activeFilterOption.length
                                ? (node) =>
                                      activeFilterOption.some((value) =>
                                          value.onFilter(node, {
                                              ...props,
                                              nodes,
                                          })
                                      )
                                : undefined
                        }
                    />
                </Box>
            </Box>
        </Stack>
    );
};

type MantineTreeProps<D, K extends string> = {
    nodes: Array<SerializableRenderTreeNode<D, K>>;
    getElements?: (
        originalNode: SerializableRenderTreeNode<D, K>
    ) => RenderTreeNodeElements;
    onContextMenu?: (selection: {
        event: MouseEvent;
        node: SerializableRenderTreeNode<D, K>;
    }) => void;
    rootExpanded?: boolean;
    activeFilterOptionFn?: (node: SerializableRenderTreeNode<D, K>) => boolean;
} & OptionalProps<{
    expandedKeys: Array<string>;
    onExpandedChanged: (selectedKeys: Array<string>) => void;
}> &
    OptionalProps<{
        selectedNodeKey: string | undefined;
        onNodeSelected: (node: SerializableRenderTreeNode<D, K>) => void;
    }> &
    OptionalProps<{
        checkedKeys: Array<string>;
        onCheckedChanged: (newValue: Array<string>) => void;
    }> &
    OptionalProps<{
        onCheckedChangedWithNode?: (
            newValue: Array<string>,
            node: SerializableRenderTreeNode<D, K>,
            checked: boolean
        ) => void;
    }>;

// TODO toggleChecked multiple with shift key
const MantineTree = <D, K extends string>({
    nodes,
    onContextMenu,
    rootExpanded,
    expandedKeys,
    onExpandedChanged,
    selectedNodeKey,
    onNodeSelected,
    checkedKeys: _checkedKeys,
    onCheckedChanged,
    onCheckedChangedWithNode,
    activeFilterOptionFn,
    getElements,
}: MantineTreeProps<D, K>) => {
    const theme = useMantineTheme();
    const systemColorScheme = useColorScheme();
    const { colorScheme: mantineColorScheme } = useMantineColorScheme();

    const internalOnCheckedChange = onCheckedChangedWithNode
        ? (newValue: Array<string>) => {
              const newId = newValue.find(
                  (value) => !checkedKeys.includes(value)
              );
              const removedId = checkedKeys.find(
                  (value) => !newValue.includes(value)
              );

              const node = visibleNodes.find(
                  (value) => value.originalNode.key === (newId ?? removedId)
              )?.originalNode;
              if (node) {
                  onCheckedChangedWithNode(newValue, node, newId !== undefined);
              }
          }
        : onCheckedChanged;

    const [checkedKeys, setCheckedKeys] = useExternalState({
        defaultValue: [],
        value: _checkedKeys,
        onChange: internalOnCheckedChange,
    });
    const [openedKeys, setOpenedKeys] = useExternalState({
        defaultValue: [],
        value: expandedKeys,
        onChange: onExpandedChanged,
    });

    const visibleNodes = useMemo(() => {
        const flatten = <D,>(
            nodes: Array<SerializableRenderTreeNode<D, K>>,
            depth = 0
        ): Array<FlattenedNode<D, K>> => {
            const alwaysOpened = rootExpanded && depth === 0;

            return nodes.flatMap((originalNode) => {
                const { key, children } = originalNode;
                const opened =
                    alwaysOpened || openedKeys.some((k) => k === key);

                const newNode: FlattenedNode<D, K> = {
                    originalNode,
                    depth,
                    opened,
                };

                if (opened && children?.length) {
                    return [newNode, ...flatten(children, depth + 1)];
                } else {
                    return newNode;
                }
            });
        };
        return flatten(nodes);
    }, [nodes, openedKeys, rootExpanded]);

    // TODO try to do this nicer
    const [keyToScrollTo, setKeyToScrollTo] = useState<string | null>(null);
    const [indexToScrollTo, setIndexToScrollTo] = useState<number | null>(null);
    const previousKeyToScrollTo = useRef<string | null>(keyToScrollTo);
    useEffect(() => {
        if (selectedNodeKey) {
            const nodesToOpen = nodesToOpenAtNode(selectedNodeKey, nodes);
            if (nodesToOpen) {
                if (nodesToOpen.length) {
                    setOpenedKeys((prevState) =>
                        Array.from(new Set([...prevState, ...nodesToOpen]))
                    );
                }
                setKeyToScrollTo(selectedNodeKey);
            }
        }
    }, [nodes, selectedNodeKey]);
    useEffect(() => {
        if (!keyToScrollTo || previousKeyToScrollTo.current === keyToScrollTo) {
            return;
        }
        const index = visibleNodes.findIndex(
            (node) => node.originalNode.key === keyToScrollTo
        );
        if (index !== -1) {
            setIndexToScrollTo(index);
        }
        previousKeyToScrollTo.current = keyToScrollTo;
    }, [keyToScrollTo, visibleNodes]);

    return (
        <VirtualizedList
            list={visibleNodes}
            fixHeight={NODE_HEIGHT}
            indexToScrollTo={indexToScrollTo}
        >
            {(props) => (
                <MantineTreeNode
                    theme={theme}
                    colorScheme={
                        mantineColorScheme === 'auto'
                            ? systemColorScheme
                            : mantineColorScheme
                    }
                    activeFilterOptionFn={activeFilterOptionFn}
                    onContextMenu={onContextMenu}
                    rootExpanded={rootExpanded}
                    selectedNodeKey={selectedNodeKey}
                    setOpenedKeys={setOpenedKeys}
                    setCheckedKeys={setCheckedKeys}
                    onNodeSelected={onNodeSelected}
                    selected={selectedNodeKey === props.originalNode.key}
                    checked={checkedKeys.some(
                        (k) => k === props.originalNode.key
                    )}
                    getElements={getElements}
                    {...props}
                />
            )}
        </VirtualizedList>
    );
};

const CHEVRON_SIZE = 22;
const CHECKBOX_HEIGHT = 20;
const CHECKBOX_WIDTH = 26;

const MantineTreeNode = <D, K extends string>({
    originalNode,
    depth,
    opened,
    setOpenedKeys,
    selected,
    onNodeSelected,
    checked,
    setCheckedKeys,
    theme,
    colorScheme,
    onContextMenu,
    rootExpanded,
    selectedNodeKey,
    activeFilterOptionFn,
    getElements,
}: FlattenedNode<D, K> &
    Pick<
        MantineTreeProps<D, K>,
        | 'getElements'
        | 'selectedNodeKey'
        | 'onNodeSelected'
        | 'onContextMenu'
        | 'rootExpanded'
        | 'activeFilterOptionFn'
    > & {
        selected: boolean;
        checked: boolean;
        setOpenedKeys: SetState<Array<string>>;
        setCheckedKeys: SetState<Array<string>>;
        theme: MantineTheme;
        colorScheme: Exclude<MantineColorScheme, 'auto'>;
    }) => {
    const [hovered, { open: onHover, close: onUnhover }] = useDisclosure(false);

    const alwaysOpened = rootExpanded && depth === 0;

    const toggleSelected = useCallback(() => {
        if (
            !originalNode.loading &&
            !originalNode.selectionDisabled &&
            originalNode.key !== selectedNodeKey
        ) {
            onNodeSelected?.(originalNode);
        }
    }, [onNodeSelected, originalNode, selectedNodeKey]);

    const toggleOpened = useCallback(() => {
        // TODO also remove children keys from openedKeys on close?
        if (originalNode.children?.length) {
            setOpenedKeys((prevKeys) => {
                const filteredKeys = prevKeys.filter(
                    (k) => k !== originalNode.key
                );

                if (filteredKeys.length === prevKeys.length) {
                    filteredKeys.push(originalNode.key);
                }
                return filteredKeys;
            });
        }
    }, [originalNode.children?.length, originalNode.key, setOpenedKeys]);

    const toggleChecked = useCallback(() => {
        if (originalNode.checkBoxSelection) {
            setCheckedKeys((prevKeys) => {
                const filteredKeys = prevKeys.filter(
                    (k) => k !== originalNode.key
                );

                if (filteredKeys.length === prevKeys.length) {
                    filteredKeys.push(originalNode.key);
                }
                return filteredKeys;
            });
        }
    }, [originalNode.checkBoxSelection, originalNode.key, setCheckedKeys]);

    const elements = originalNode.loading
        ? undefined
        : getElements?.(originalNode);

    const expansionOpenIcon = elements?.expansionOpenIcon;
    const expansionCloseIcon = elements?.expansionCloseIcon;
    const icon = elements?.icon;
    const hoverNode = elements?.hoverNode;
    const permanentNode = elements?.permanentNode;
    const onDoubleClick = elements?.onDoubleClick;

    const chevron = opened
        ? (expansionOpenIcon ?? <IconChevronDown size={18} />)
        : (expansionCloseIcon ?? <IconChevronRight size={18} />);

    return (
        <Box
            style={{
                cursor: originalNode.selectionDisabled ? undefined : 'pointer',
                borderRadius: getRadius('sm'),
            }}
            h={NODE_HEIGHT}
            ml={depth * CHEVRON_SIZE}
            bg={
                selected
                    ? theme.colors[theme.primaryColor]?.[
                          colorScheme === 'light' ? 1 : 9
                      ]
                    : hovered
                      ? theme.colors.gray[colorScheme === 'light' ? 2 : 8]
                      : undefined
            }
            onMouseEnter={onHover}
            onMouseLeave={onUnhover}
            onClick={toggleSelected}
            onDoubleClick={() => {
                if (!alwaysOpened) {
                    toggleOpened();
                }
                if (!originalNode.loading) {
                    onDoubleClick?.();
                }
            }}
            onContextMenu={(e) => {
                e.preventDefault();
                if (!originalNode.loading) {
                    onContextMenu?.({ event: e, node: originalNode });
                }
            }}
        >
            <Group
                gap={0}
                wrap={'nowrap'}
            >
                {originalNode.children?.length ? (
                    alwaysOpened ? (
                        <Flex
                            c={'inherit'}
                            h={CHEVRON_SIZE}
                            w={CHEVRON_SIZE}
                            align={'center'}
                            justify={'center'}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {chevron}
                        </Flex>
                    ) : (
                        <ActionIcon
                            c={'inherit'}
                            size={CHEVRON_SIZE}
                            variant={'subtle'}
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleOpened();
                            }}
                        >
                            {chevron}
                        </ActionIcon>
                    )
                ) : (
                    <Box
                        w={CHEVRON_SIZE}
                        miw={CHEVRON_SIZE}
                    />
                )}
                {originalNode.checkBoxSelection && (
                    <Checkbox
                        h={CHECKBOX_HEIGHT}
                        w={CHECKBOX_WIDTH}
                        px={(NODE_HEIGHT - CHECKBOX_HEIGHT) / 2}
                        checked={checked}
                        disabled={originalNode.checkBoxDisabled === true}
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleChecked();
                        }}
                    />
                )}
                {icon && (
                    <Box
                        mah={NODE_HEIGHT - 4}
                        pr={8}
                        style={{ transform: 'scale(0.8)' }}
                    >
                        {icon}
                    </Box>
                )}
                <Text
                    className={
                        originalNode.loading ? 'tree-loading' : undefined
                    }
                    style={{
                        ...originalNode.style,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        userSelect: 'none',
                        opacity:
                            activeFilterOptionFn &&
                            !activeFilterOptionFn(originalNode)
                                ? 0.4
                                : undefined,
                    }}
                >
                    {originalNode.label}
                </Text>
                <Box style={{ flexGrow: 1 }} />
                {(hovered || selected) && hoverNode && (
                    <Box
                        mah={NODE_HEIGHT - 4}
                        style={{ transform: 'scale(0.8)' }}
                    >
                        {hoverNode}
                    </Box>
                )}
                {permanentNode && (
                    <Box
                        mah={NODE_HEIGHT - 4}
                        style={{ transform: 'scale(0.8)' }}
                    >
                        {permanentNode}
                    </Box>
                )}
            </Group>
        </Box>
    );
};
